import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Upload, X } from 'lucide-react';
import { cn } from '@/utils/cn';
import { Button, IconButton, Progress } from '@/components/ds';
import { useToast } from '@/contexts/ToastContext';
import { isBusy, useActiveImports, useCanImport } from '@/hooks/useImports';
import {
  ENTITY_ROUTES,
  MONEY_ENTITIES,
  jobPath,
  type ImportJob,
} from '@/api/imports.api';

/**
 * The import keeps running wherever the user goes; this corner card is how
 * they see it move. It appears only while a job is working or waiting on
 * them, and says when one finishes. Hidden on the import pages themselves,
 * which show the full picture.
 */
/** Records written; for money imports "merged" rows are lines of an invoice. */
const written = (j: ImportJob) =>
  MONEY_ENTITIES.includes(j.entityType)
    ? j.stats.created
    : j.stats.created + j.stats.merged;

export function ImportProgressDock() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { showSuccess } = useToast();
  // Any kind of import: the dock follows whatever is running. Income and
  // expenses share one right (finance).
  const canPatients = useCanImport('patients');
  const canFinance = useCanImport('income');
  const canImport = canPatients || canFinance;
  const { data } = useActiveImports(canImport);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const seenBusy = useRef<Set<string>>(new Set());

  // A job that was working and now is not: say so once.
  useEffect(() => {
    const nf = new Intl.NumberFormat(i18n.language);
    for (const j of data?.items ?? []) {
      if (isBusy(j)) seenBusy.current.add(j.id);
      else if (seenBusy.current.has(j.id)) {
        seenBusy.current.delete(j.id);
        if (j.status === 'completed')
          showSuccess(
            t(`dataImport.dock.completedToastBy.${j.entityType}`, {
              count: written(j),
              formatted: nf.format(written(j)),
            }),
          );
        if (j.status === 'review')
          showSuccess(t('dataImport.dock.readyToReview', { file: j.fileName }));
      }
    }
  }, [data, showSuccess, t, i18n.language]);

  if (
    !canImport ||
    Object.values(ENTITY_ROUTES).some((r) => pathname.startsWith(r.home))
  )
    return null;
  // Working, or waiting on the user. A finished import is announced by the
  // toast above and then gets out of the way.
  const jobs = (data?.items ?? []).filter(
    (j) =>
      !dismissed.has(j.id) &&
      (isBusy(j) || j.status === 'review' || j.status === 'mapping'),
  );
  if (!jobs.length) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed start-4 end-4 bottom-20 z-(--z-drawer-scrim) space-y-2 sm:start-auto sm:w-88 lg:bottom-4 print:hidden"
    >
      {jobs.slice(0, 2).map((j, i) => {
        const pct =
          j.progress.total > 0
            ? Math.round((j.progress.done / j.progress.total) * 100)
            : 0;
        const waiting = j.status === 'review' || j.status === 'mapping';
        return (
          <div
            key={j.id}
            className={cn(
              'rounded-lg border border-neutral-200 bg-surface-1 px-3 py-2 shadow-xl',
              // One at a time on a phone: the list underneath must stay usable.
              i > 0 && 'hidden sm:block',
            )}
          >
            <div className="flex items-center gap-2">
              <Upload className="size-4 shrink-0 text-muted" aria-hidden />
              <div className="min-w-0 flex-1">
                <p className="truncate text-small font-semibold text-foreground">
                  <bdi>{j.fileName}</bdi>
                </p>
                <p className="num truncate text-small text-muted">
                  {t(`dataImport.status.${j.status}`)}
                  {isBusy(j) && j.progress.total > 0 ? ` · ${pct}%` : ''}
                </p>
              </div>
              <Button
                size="sm"
                variant={waiting ? 'secondary' : 'ghost'}
                onPress={() => navigate(jobPath(j))}
              >
                {waiting
                  ? t('dataImport.dock.review')
                  : t('dataImport.dock.open')}
              </Button>
              <IconButton
                size="sm"
                variant="ghost"
                aria-label={t('common.dismiss', 'Dismiss')}
                onPress={() => setDismissed((d) => new Set(d).add(j.id))}
              >
                <X className="size-3.5" />
              </IconButton>
            </div>
            {isBusy(j) && (
              <Progress
                value={pct}
                size="sm"
                label={t(`dataImport.status.${j.status}`)}
                hideLabel
                className="mt-2"
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
